<template>
  <transition name="fade">
    <div v-if="visible" class="preview">
      <div v-if="isSuccess">
        <v-layout class="mb-2">
          <v-flex style="font-size: 10px" class="text-uppercase" xs7>
            {{ $t(`${namespace}.service`) }}
          </v-flex>

          <v-flex style="font-size: 10px" class="text-xs-right" xs5>
            {{ $t('Common.currency') }}
          </v-flex>
        </v-layout>

        <v-layout>
          <v-flex xs7>{{ $t(`${namespace}.rides`) }}</v-flex>
          <v-flex class="text-xs-right" xs5>
            <strong>{{ data.ridesSubtotal | currencyBRLwithoutSymbol }}</strong>
          </v-flex>
        </v-layout>

        <v-layout v-if="data.saasSubtotal">
          <v-flex xs7>{{ $t(`${namespace}.serviceCharge`) }}</v-flex>
          <v-flex class="text-xs-right" xs5>
            <span
              v-if="(hasRole('profiles', 'ROLE_ADMINISTRATOR') || hasRole('profiles', 'ROLE_FINANCIAL_ADMIN')) && data.saasSubtotal"
              class="reset"
              @click="toggleResetValue('serviceCharge')"
            >
              <span v-if="data.applyServiceCharge">{{ $t(`${namespace}.reset`) }}</span>
              <span v-else>{{ $t(`${namespace}.recalculate`) }}</span>
            </span>

            <span>
              <strong v-if="data.applyServiceCharge">
                {{ data.saasSubtotal | currencyBRLwithoutSymbol }}
              </strong>
              <span class="ml-1" v-else>{{ $t(`${namespace}.free`) }}</span>
            </span>
          </v-flex>
        </v-layout>

        <v-layout v-if="data.chargeBacksSubtotal">
          <v-flex class="success--text" xs7>{{ $t(`${namespace}.reversedRides`) }}</v-flex>
          <v-flex class="text-xs-right" xs5>
            <strong class="success--text">
              {{ data.chargeBacksSubtotal | currencyBRLwithoutSymbol }}
            </strong>
          </v-flex>
        </v-layout>

        <v-layout v-if="data.discountsSubtotal">
          <v-flex class="success--text" xs7>{{ $t(`${namespace}.contractedDiscount`) }}</v-flex>
          <v-flex class="text-xs-right" xs5>
            <strong class="success--text">
              {{ data.discountsSubtotal | currencyBRLwithoutSymbol }}
            </strong>
          </v-flex>
        </v-layout>

        <div v-if="data.creditsSubtota">
          <hr class="my-3" style="opacity: 0.3">

          <v-layout>
            <v-flex xs7>{{ $t(`${namespace}.subtotal`) }}</v-flex>
            <v-flex class="text-xs-right" xs5>
              <strong>
                {{ ((data.ridesSubtotal + data.saasSubtotal) - data.chargeBacksSubtotal - data.discountsSubtotal) | currencyBRLwithoutSymbol }}
              </strong>
            </v-flex>
          </v-layout>

          <v-layout>
            <v-flex class="success--text" xs7>{{ $t(`${namespace}.rideCredits`) }}</v-flex>
            <v-flex class="text-xs-right" xs5>
              <strong class="success--text">
                {{ data.creditsSubtotal | currencyBRLwithoutSymbol }}
              </strong>
            </v-flex>
          </v-layout>
        </div>

        <div v-if="data.penaltySubtotal || data.interestSubtotal">
          <hr class="my-3" style="opacity: 0.3">

          <v-layout>
            <v-flex xs7>{{ $t(`${namespace}.total`) }}</v-flex>
            <v-flex class="text-xs-right" xs5>
              <strong>
                {{ data.orderSubtotal | currencyBRL }}
              </strong>
            </v-flex>
          </v-layout>

          <v-layout v-if="data.penaltySubtotal">
            <v-flex class="error--text" xs7>{{ $t(`${namespace}.penalty`) }}</v-flex>
            <v-flex class="text-xs-right" xs5>
              <span
                v-if="(hasRole('profiles', 'ROLE_ADMINISTRATOR') || hasRole('profiles', 'ROLE_FINANCIAL_ADMIN')) && data.penaltySubtotal"
                class="reset"
                @click="toggleResetValue('penalty')"
              >
                <span v-if="data.applyPenalty">{{ $t(`${namespace}.reset`) }}</span>
                <span v-else>{{ $t(`${namespace}.recalculate`) }}</span>
              </span>

              <span>
                <strong v-if="data.applyPenalty" class="error--text">
                  {{ data.penaltySubtotal | currencyBRLwithoutSymbol }}
                </strong>
                <span class="ml-1" v-else>{{ $t(`${namespace}.free`) }}</span>
              </span>
            </v-flex>
          </v-layout>

          <v-layout v-if="data.interestSubtotal">
            <v-flex class="error--text" xs7>{{ $t(`${namespace}.interest`) }}</v-flex>
            <v-flex class="text-xs-right" xs5>
              <span
                v-if="(hasRole('profiles', 'ROLE_ADMINISTRATOR') || hasRole('profiles', 'ROLE_FINANCIAL_ADMIN')) && data.interestSubtotal"
                class="reset"
                @click="toggleResetValue('interest')"
              >
                <span v-if="data.applyInterest">{{ $t(`${namespace}.reset`) }}</span>
                <span v-else>{{ $t(`${namespace}.recalculate`) }}</span>
              </span>

              <span>
                <strong v-if="data.applyInterest" class="error--text">
                  {{ data.interestSubtotal | currencyBRLwithoutSymbol }}
                </strong>
                <span class="ml-1" v-else>{{ $t(`${namespace}.free`) }}</span>
              </span>
            </v-flex>
          </v-layout>
        </div>

        <hr class="my-3" style="opacity: 0.3">

        <v-layout>
          <v-flex xs7>{{ $t(`${namespace}.totalToPay`) }}</v-flex>
          <v-flex class="text-xs-right" xs5>
            <strong style="font-size: 16px">
              {{ calcDuplicateInvoiceSubtotal | currencyBRL }}
            </strong>
          </v-flex>
        </v-layout>
      </div>

      <v-layout v-if="isLoading" class="py-4" align-center justify-center row>
        <v-progress-circular indeterminate color="accent" />
      </v-layout>
    </div>
  </transition>
</template>

<script>
import { isObject, hasRole } from 'utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'

export default {
  name: 'Preview',
  props: {
    status: {
      type: Number
    },
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    },

    visible () {
      return this.isValidObject(this.data) && this.data.newDueDate && (this.isLoading || this.isSuccess)
    },

    calcDuplicateInvoiceSubtotal () {
      const applyServiceCharge = this.data.applyServiceCharge ? 0 : this.data.saasSubtotal
      const applyPenalty = this.data.applyPenalty ? 0 : this.data.penaltySubtotal
      const applyInterest = this.data.applyInterest ? 0 : this.data.interestSubtotal
      return this.data.duplicateInvoiceSubtotal - applyServiceCharge - applyPenalty - applyInterest
    }
  },
  data () {
    return {
      namespace: 'Views.InvoiceExtendDueDate.preview'
    }
  },
  methods: {
    isValidObject (val) {
      return isObject(val)
    },

    hasRole (group, role) {
      return hasRole(group, role)
    },

    toggleResetValue (field) {
      this.$emit('toggleResetValue', field)
    }
  }
}
</script>

<style lang="scss" scoped>
.preview {
  background-color: $gray-1;
  border: 1px solid $gray-4;
  border-radius: 4px;
  padding: 24px;
}

.reset {
  color: $accent;
  cursor: pointer;
  margin-right: 2px;

  &:hover {
    text-decoration: underline;
  }
}
</style>
